import { useCallback, useRef, useState } from "react";
import { ENV_VARS } from "@/constants/env";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { APIEndpoints } from "@/constants/apiEndpoints";
import { isHttpUrl } from "@/utils/file.utils";
import { AiChatType } from "@/utils/enums/ai-assistant.enum";
import type {
  IAiGeneratedAttachment,
  IAiPageContext,
} from "@/types/(my-space)/ai-assistant.interface";
import { languageHeader } from "@/i18n/request-language";
import i18n from "@/i18n";

interface StreamMeta {
  sessionId: string;
  conversationId: number;
  lastUserMessageId: number;
}

interface StreamFinal {
  answer: string;
  sessionId: string;
  // Maya interactive-form spec, when the backend asks for structured input on
  // the final event. Validated against looksLikeFormSpec by the consumer.
  form?: unknown;
  // Files the AI rendered + uploaded for this turn ([] when none), and any
  // human-readable preparation/upload failures. Independent of `success`.
  attachments: IAiGeneratedAttachment[];
  attachmentErrors: string[];
}

interface StreamMetaFinal {
  lastAiMessageId: number;
}

interface StreamCallbacks {
  onMeta: (data: StreamMeta) => void;
  onStatus: (message: string) => void;
  onFinal: (data: StreamFinal) => void;
  onMetaFinal: (data: StreamMetaFinal) => void;
  onDone: () => void;
  onError: (error: Error) => void;
}

// Quick chat answers in `response`, deep chat in `answer` / `message` — take
// the first that is a string (content/text kept as legacy fallbacks).
const pickAnswer = (data: Record<string, unknown>): string => {
  for (const key of ["response", "answer", "message", "content", "text"]) {
    if (typeof data[key] === "string") return data[key] as string;
  }
  return "";
};

const parseAttachments = (raw: unknown): IAiGeneratedAttachment[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is IAiGeneratedAttachment =>
      !!a &&
      typeof a === "object" &&
      isHttpUrl(a.url) &&
      typeof a.name === "string",
  );
};

const parseAttachmentErrors = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((e): e is string => typeof e === "string" && !!e.trim())
    : [];

const createTurnId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto)
    return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const useStreamingChat = (endpointUrl?: string) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // The turn id of the in-flight stream — used to cancel it server-side.
  const turnIdRef = useRef<string | null>(null);
  const baseUrl = ENV_VARS.API_INSTANCE.replace(/\/$/, "");
  const resolvedUrl = endpointUrl
    ? `${baseUrl}${endpointUrl}`
    : `${baseUrl}${APIEndpoints.MySpace.AIAssistant.SmartChat}`;
  // Cancel always targets the main chat-stream/cancel for this realm. The AI
  // service cancels by turn_id, so this works for retries (a different stream
  // URL) too — the realm/auth is what must match.
  const cancelUrl = `${resolvedUrl}/cancel`;

  const stream = useCallback(
    async (
      url: string,
      body: Record<string, unknown>,
      callbacks: StreamCallbacks,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // Stamp this turn so the server can cancel it via /chat-stream/cancel.
      const turnId = createTurnId();
      turnIdRef.current = turnId;
      body.turnId = turnId;
      setIsStreaming(true);

      const STREAM_IDLE_TIMEOUT_MS = 90_000;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let errorDelivered = false;
      let finalDelivered = false;
      let doneDelivered = false;

      const deliverError = (err: Error) => {
        if (errorDelivered) return;
        errorDelivered = true;
        callbacks.onError(err);
      };

      const resetIdleTimer = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          deliverError(new Error(i18n.t("common:streaming.timed_out")));
          controller.abort();
        }, STREAM_IDLE_TIMEOUT_MS);
      };

      try {
        const authTokens = getAuthTokensFromLocalStorage();
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authTokens
              ? { Authorization: `Bearer ${authTokens.accessToken}` }
              : {}),
            ...languageHeader(),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const err = (await response.json().catch(() => ({}))) as Record<
            string,
            unknown
          >;
          throw new Error(
            (err?.message as string) ?? `HTTP ${response.status}`,
          );
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        resetIdleTimer();

        while (true) {
          if (controller.signal.aborted) {
            await reader.cancel().catch(() => {});
            break;
          }
          const { done, value } = await reader.read();
          if (done) break;
          resetIdleTimer();
          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";

          for (const part of parts) {
            if (controller.signal.aborted) break;
            if (!part.trim()) continue;
            let eventType = "";
            let dataStr = "";
            for (const line of part.split("\n")) {
              if (line.startsWith("event:")) eventType = line.slice(6).trim();
              else if (line.startsWith("data:")) dataStr = line.slice(5).trim();
            }
            if (!eventType || !dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (eventType === "meta") callbacks.onMeta(data as StreamMeta);
              else if (eventType === "status")
                callbacks.onStatus(data.message ?? data.status ?? "");
              else if (eventType === "final") {
                finalDelivered = true;
                callbacks.onFinal({
                  answer: pickAnswer(data),
                  sessionId: data.sessionId ?? "",
                  form: data.form,
                  attachments: parseAttachments(data.attachments),
                  attachmentErrors: parseAttachmentErrors(
                    data.attachment_errors,
                  ),
                });
              } else if (eventType === "meta-final")
                callbacks.onMetaFinal(data as StreamMetaFinal);
              else if (eventType === "done") {
                doneDelivered = true;
                callbacks.onDone();
              } else if (eventType === "error") {
                const msg =
                  (data?.message as string) ??
                  (data?.error as string) ??
                  "Stream failed";
                deliverError(new Error(msg));
              }
            } catch {
              // malformed SSE event — skip
            }
          }
        }

        if (!finalDelivered && !doneDelivered) {
          deliverError(new Error(i18n.t("common:streaming.connection_lost")));
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          deliverError(err as Error);
        }
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        setIsStreaming(false);
      }
    },
    [],
  );

  const sendMessage = useCallback(
    (
      payload: {
        message: string;
        sessionId?: string;
        isTemporary?: boolean;
        chatType?: AiChatType;
        attachmentUrls?: string[];
        /** The app page the user is looking at (Maya sidebar). */
        pageContext?: IAiPageContext;
      },
      callbacks: StreamCallbacks,
    ) => stream(resolvedUrl, payload as Record<string, unknown>, callbacks),
    [resolvedUrl, stream],
  );

  const retryMessage = useCallback(
    (
      retryEndpointUrl: string,
      // Persisted chats use the integer DB id; temp chats use the Redis hex id.
      assistantMessageId: number | string,
      callbacks: StreamCallbacks,
      attachmentUrls?: string[],
      isTemporary?: boolean,
      sessionId?: string,
    ) =>
      stream(
        `${baseUrl}${retryEndpointUrl}`,
        {
          assistantMessageId,
          // Temp chats keep their context in Redis keyed by sessionId, so retry
          // must carry both the isTemporary flag and the sessionId — otherwise
          // the server can't find the message context and the regenerate 4xxs.
          ...(isTemporary !== undefined ? { isTemporary } : {}),
          ...(isTemporary && sessionId ? { sessionId } : {}),
          ...(attachmentUrls?.length ? { attachmentUrls } : {}),
        },
        callbacks,
      ),
    [baseUrl, stream],
  );

  // Abort only the local connection (no server-side cancel).
  const abort = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  // Stop generation: tell the AI service to cancel this turn (best-effort,
  // fire-and-forget) AND abort the local stream so the UI stops immediately.
  const cancel = useCallback(() => {
    const turnId = turnIdRef.current;
    if (turnId) {
      const authTokens = getAuthTokensFromLocalStorage();
      void fetch(cancelUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authTokens
            ? { Authorization: `Bearer ${authTokens.accessToken}` }
            : {}),
          ...languageHeader(),
        },
        body: JSON.stringify({ turnId }),
        keepalive: true,
      }).catch(() => {
        // best-effort; the local abort below already stopped the UI
      });
    }
    turnIdRef.current = null;
    abortRef.current?.abort();
    setIsStreaming(false);
  }, [cancelUrl]);

  return { sendMessage, retryMessage, isStreaming, abort, cancel };
};
